import React, { useEffect, useCallback } from 'react';
import { Box, Button, Typography, Divider, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useSetRecoilState, useRecoilState } from 'recoil';
import { StopCardList } from './StopCardList';
import { atom_activeStopState } from '@app/shared/state/active-stop.atom';
import { atom_bookmarkedStopsState, atom_selectedFilterState, atom_allBookmarksState } from '@app/shared/state/filter.atom';
import { atom_tourState, atom_originalTourState } from '@app/shared/state/tour.atom';
import { sideBarStateAtom } from '@app/shared/state/sidebar.atom';
import { atom_listModeState } from '@app/shared/state/list-mode.atom';
import { atom_userAddedPlaces, atom_selectedUserPlace, PlaceDetails } from '@app/shared/state/user-added-stops.atom';
import { atom_modalType, atom_showModalContent, atom_navigationHelperState } from '@app/shared/state/show-content-modal.atom';
import DirectionIcon from '@app/new-app/direction.svg';

interface DesktopSavedContentProps {
  isHebrew: () => boolean;
  isListMode: boolean;
  contentRef: React.RefObject<HTMLDivElement>;
  preventMapInteraction: (e: React.MouseEvent | React.TouchEvent) => void;
  mapref?: React.RefObject<any>;
  flyToSelectedStop?: (stopId: string) => void;
}

export const DesktopSavedContent: React.FC<DesktopSavedContentProps> = ({
  isHebrew,
  isListMode,
  contentRef,
  preventMapInteraction,
  mapref,
  flyToSelectedStop
}) => {
  const { t, i18n } = useTranslation();

  // Detect RTL language
  const isRTL = i18n.language === 'he';

  // Import atoms
  const activeStopState = useRecoilValue(atom_activeStopState);
  const activeStopId = activeStopState?.id;
  const bookmarkedStops = useRecoilValue(atom_bookmarkedStopsState);
  const tourState = useRecoilValue(atom_tourState);
  const originalTour = useRecoilValue(atom_originalTourState);
  const setSideBarState = useSetRecoilState(sideBarStateAtom);
  const setTourState = useSetRecoilState(atom_tourState);
  const setListMode = useSetRecoilState(atom_listModeState);
  const selectedFilters = useRecoilValue(atom_selectedFilterState);
  const userAddedPlaces = useRecoilValue(atom_userAddedPlaces);
  const [allBookmarks, setAllBookmarks] = useRecoilState(atom_allBookmarksState);
  const setSelectedUserPlace = useSetRecoilState(atom_selectedUserPlace);
  const setNavState = useSetRecoilState(atom_navigationHelperState);
  const setActiveStop = useSetRecoilState(atom_activeStopState);
  const setModalType = useSetRecoilState(atom_modalType);
  const setShowModal = useSetRecoilState(atom_showModalContent);

  // Get user added stops for current tour that are bookmarked
  const tourId = tourState?.id;
  const userStops = tourId ? (userAddedPlaces[tourId] || []) : [];
  const bookmarkedUserStops = userStops.filter(place => bookmarkedStops.includes(place.place_id));

  // Total saved count
  const totalSavedCount = bookmarkedStops.length;

  // Function to exit saved mode (selector_visibleStops handles filtering)
  const exitSavedMode = useCallback(() => {
    setSideBarState(null);
    setListMode('desktop');
  }, [setSideBarState, setListMode]);

  // Toggle bookmark for a user stop
  const handleToggleBookmark = (place: PlaceDetails) => {
    if (!tourId) return;

    const currentTourBookmarks = allBookmarks[tourId] || [];
    if (currentTourBookmarks.includes(place.place_id)) {
      // Remove from bookmarks
      const updatedBookmarks = currentTourBookmarks.filter(id => id !== place.place_id);
      setAllBookmarks({
        ...allBookmarks,
        [tourId]: updatedBookmarks,
      });
    } else {
      // Add to bookmarks
      setAllBookmarks({
        ...allBookmarks,
        [tourId]: [...currentTourBookmarks, place.place_id],
      });
    }
  };

  // Handle directions for a user stop
  const handleDirections = (place: PlaceDetails, e: React.MouseEvent<HTMLElement>) => {
    const stopForNav = {
      stop_location: {
        latitude: place.geometry.location.lat,
        longitude: place.geometry.location.lng,
      },
      s_title: place.name,
      location: {
        latitude: place.geometry.location.lat,
        longitude: place.geometry.location.lng,
      },
    };
    setActiveStop(stopForNav as any);
    setNavState({
      anchorEl: e.currentTarget,
      offset: { top: 0, x: -15 },
      usePopover: true,
    });
  };

  // Handle show more for a user stop - open search place modal for editing
  const handleShowMore = (place: PlaceDetails) => {
    setSelectedUserPlace(place);
    setModalType('searchPlace');
    setShowModal(true);
  };

  // Reset scroll to top on mount
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, []);

  // Scroll selected card into view
  useEffect(() => {
    if (!activeStopId) return;
    if (!contentRef.current) return;

    const skipScroll = (window as any).__skipListScroll;
    if (skipScroll) {
      (window as any).__skipListScroll = false;
      return;
    }

    const cardNode = document.getElementById(activeStopId);
    if (cardNode) {
      contentRef.current.scrollTo({
        top: cardNode.offsetTop - 45,
        behavior: 'smooth',
      });
    }
  }, [activeStopId, contentRef]);

  // Handle card click - fly to location
  const handleCardClick = (place: PlaceDetails) => {
    if (mapref?.current) {
      mapref.current.flyTo([place.geometry.location.lat, place.geometry.location.lng], 17, { duration: 1 });
    }
  };

  // User Stop Card component
  const UserStopCard = ({ place }: { place: PlaceDetails }) => {
    return (
      <Box
        onClick={() => handleCardClick(place)}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          mb: 1.5,
          cursor: 'pointer',
        }}
      >
        {/* Card Body */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'white',
            borderRadius: '8px',
            boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
            overflow: 'hidden',
          }}
        >
          {/* Card Content */}
          <Box sx={{ p: 2 }}>
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 600,
                color: '#333',
                mb: 0.5,
              }}
            >
              {place.name}
            </Typography>
            {place.description && (
              <Typography
                sx={{
                  fontSize: '14px',
                  color: '#666',
                  mb: 0.5,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {place.description}
              </Typography>
            )}
            {place.formatted_address && (
              <Typography
                sx={{
                  fontSize: '12px',
                  color: '#999',
                }}
              >
                {place.formatted_address}
              </Typography>
            )}
          </Box>

          <Divider />

          {/* Card Actions */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 2,
              py: 1,
            }}
          >
            <Box
              onClick={(e) => { e.stopPropagation(); handleShowMore(place); }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#333',
                }}
              >
                {t('new_app.explore_more') || 'Show more'}
              </Typography>
              <i
                className={isRTL ? "fa-solid fa-angle-left" : "fa-solid fa-angle-right"}
                style={{ fontSize: '14px', color: '#333' }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); handleDirections(place, e); }}
                sx={{ p: 0.5 }}
              >
                <img src={DirectionIcon} alt="" aria-hidden="true" style={{ width: '21px', height: '21px' }} />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box
      dir={isRTL ? 'rtl' : 'ltr'}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        pt: 1,
        position: 'relative',
        backgroundColor: tourState?.config?.listColor ? `#${tourState.config.listColor.replace('#', '')}` : '#F6F6F6',
      }}
      onClick={preventMapInteraction}
      onTouchStart={preventMapInteraction}
      onTouchMove={preventMapInteraction}
      onMouseDown={preventMapInteraction}
      onMouseMove={preventMapInteraction}
    >
      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        px: 2,
        py: 1,
        minHeight: 48
      }}>
        <Typography sx={{
          fontWeight: 700,
          fontSize: '18px',
          lineHeight: '20px',
          letterSpacing: '0px',
          verticalAlign: 'middle'
        }}>
          {(t('saved') || 'Saved') + (totalSavedCount ? ` (${totalSavedCount})` : '')}
        </Typography>
        <Button
          onClick={exitSavedMode}
          aria-label={t('close') || 'Close'}
          variant="text"
          disableRipple
          sx={{
            minWidth: 'auto',
            p: 0.5,
            color: '#48526B',
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'transparent',
              color: '#333',
            }
          }}
        >
          <i className="fa-regular fa-xmark" style={{ fontSize: '20px' }} />
        </Button>
      </Box>

      <Divider sx={{ color: '#E3E3E3' }} />

      {/* Stop Cards List */}
      <Box
        component="nav"
        ref={contentRef}
        role="navigation"
        aria-label={t('accessibility.saved_stops_list') as string}
        sx={{
          flex: 1,
          minHeight: 0,
          maxHeight: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          px: 1.5,
          pt: 1.5,
          pb: 2,
          '&::-webkit-scrollbar': {
            width: '8px',
            backgroundColor: '#f1f1f1'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#888',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: '#555'
            }
          },
          scrollbarWidth: 'thin',
          scrollbarColor: '#888 #f1f1f1',
        }}
      >
        <StopCardList
          mode="desktop"
          isHebrew={isHebrew}
          isListMode={isListMode}
          preventMapInteraction={preventMapInteraction}
          mapref={mapref}
          flyToSelectedStop={flyToSelectedStop}
        />

        {/* Bookmarked User Stops */}
        {bookmarkedUserStops.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '16px',
                lineHeight: '20px',
                mb: 1.5,
                px: 0.5,
              }}
            >
              {t('your_places') || 'Added Places'}
            </Typography>
            {bookmarkedUserStops.map((place) => (
              <UserStopCard key={place.place_id} place={place} />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};
